import { cn, ScrollShadowX, Separator, TabsList, TabsTrigger } from '@1trading/react';
import { Lock } from 'lucide-react';
import { Fragment, useEffect, useRef } from 'react';
import { EYEBROW_CLASS } from '@/components/ui/labeled-value';
import type { UserTabGroupWithTabs } from './tab-registry';

const TRIGGER_CLASS = cn(
  // Below xl: underline strip, distinct from the grey pill sub-tabs inside panels
  'h-10 gap-2 rounded-none border-b-2 border-transparent bg-transparent px-2.5 text-muted-foreground hover:bg-transparent hover:text-foreground',
  'data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground',
  // xl rail: full-width rows with a filled active state
  'xl:h-8 xl:w-full xl:justify-start xl:rounded-md xl:border-b-0 xl:hover:bg-muted/60 xl:data-[state=active]:bg-muted xl:data-[state=active]:font-semibold',
);

interface NavGroupProps {
  group: UserTabGroupWithTabs;
}

const NavGroup = ({ group }: NavGroupProps) => (
  <div className="flex shrink-0 flex-col">
    <span aria-hidden className={cn(EYEBROW_CLASS, 'hidden px-2.5 pb-1 xl:block')}>
      {group.label}
    </span>
    <TabsList aria-label={group.label} className="gap-0.5 xl:w-full xl:flex-col xl:items-stretch" variant="floating">
      {group.tabs.map(({ value, label, icon: Icon, sensitive }) => (
        <TabsTrigger className={TRIGGER_CLASS} data-tab={value} key={value} value={value}>
          <Icon aria-hidden className="size-4 shrink-0" />
          {label}
          {sensitive ? (
            <>
              <Lock aria-hidden className="size-3 shrink-0 text-muted-foreground xl:ml-auto" />
              <span className="sr-only">(requires justification)</span>
            </>
          ) : null}
        </TabsTrigger>
      ))}
    </TabsList>
  </div>
);
NavGroup.displayName = 'NavGroup';

interface UserSectionNavProps {
  groups: readonly UserTabGroupWithTabs[];
  activeTab: string;
  /** Vertical rail at xl; horizontal scrolling strip below */
  isRail: boolean;
}

export const UserSectionNav = ({ groups, activeTab, isRail }: UserSectionNavProps) => {
  const navRef = useRef<HTMLElement>(null);

  // Keep a deep-linked or keyboard-selected tab visible in the horizontal strip
  useEffect(() => {
    navRef.current?.querySelector(`[data-tab="${activeTab}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  return (
    <nav
      aria-label="User sections"
      className="sticky top-0 z-20 -mx-6 border-b bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/80 xl:top-0 xl:mx-0 xl:w-56 xl:shrink-0 xl:self-start xl:border-0 xl:bg-transparent xl:px-0 xl:pt-1 xl:backdrop-blur-none"
      ref={navRef}
    >
      {isRail ? (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <NavGroup group={group} key={group.id} />
          ))}
        </div>
      ) : (
        // ScrollShadow sizes its single child to max-content, so it only wraps the horizontal strip
        <ScrollShadowX>
          <div className="flex items-center gap-1">
            {groups.map((group, index) => (
              <Fragment key={group.id}>
                {index > 0 ? <Separator className="mx-1 h-5" orientation="vertical" /> : null}
                <NavGroup group={group} />
              </Fragment>
            ))}
          </div>
        </ScrollShadowX>
      )}
    </nav>
  );
};
UserSectionNav.displayName = 'UserSectionNav';
